/* eslint-disable eqeqeq */
import {
  BareInput,
  BareSelect,
  CatalogueChrome,
  cmsList,
  InputRow,
  SelectRow,
  type OptionRow,
} from '../formKit';
import { formatIntegerDisplay } from '../formUtils';
import { useAppSelector } from 'app/hooks';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import type { AccessoryFormApi } from './useAccessoryForm';

/**
 * Phase 6e2b-1 — accessory field wrappers over the §52.1 formKit primitives,
 * bound to the AccessoryFormApi commits (the accessory-variant §49.6 field
 * binding: clamp-on-commit, params-filter writes, selection mirrors).
 * Mirrors formCommon.tsx (component forms) without coupling the two APIs.
 */

export const accList = (
  doc: Record<string, unknown> | undefined,
  path: string
): OptionRow[] => cmsList(doc, path);

interface BaseProps {
  api: AccessoryFormApi;
  name: string;
  label?: React.ReactNode;
  unit?: string;
  required?: boolean;
}

/** model_field select row (li > div + selection mirror). */
export const ASelect = ({
  api,
  name,
  label,
  unit,
  required,
  options,
  cmsPath,
  labelKey,
  valueKey,
  disabled,
  rowClass,
  extraClass,
  selectionClass,
  hideNone,
  noneMode,
  modelField,
  children,
}: BaseProps & {
  options?: OptionRow[];
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  disabled?: boolean;
  rowClass?: string;
  extraClass?: string;
  selectionClass?: string;
  hideNone?: boolean;
  noneMode?: 'visible' | 'hidden' | 'omit';
  modelField?: string;
  children?: React.ReactNode;
}) => (
  <SelectRow
    name={name}
    fieldClass={`model_field${extraClass ? ` ${extraClass}` : ''}`}
    value={api.model?.get(modelField ?? name)}
    options={options ?? accList(api.cmsAccessory, cmsPath ?? name)}
    labelKey={labelKey}
    valueKey={valueKey}
    label={label}
    unit={unit}
    required={required}
    disabled={disabled}
    rowClass={rowClass ?? 'single-line'}
    selectionClass={selectionClass}
    hideNone={hideNone}
    noneMode={noneMode}
    onChange={(raw) => api.commitModelField(name, raw)}
  >
    {children}
  </SelectRow>
);

/** bare model_field select (multi-control rows). */
export const ABareSelect = ({
  api,
  name,
  options,
  cmsPath,
  labelKey,
  valueKey,
  extraClass,
  selectionClass,
  disabled,
  hideNone,
}: {
  api: AccessoryFormApi;
  name: string;
  options?: OptionRow[];
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  extraClass?: string;
  selectionClass?: string;
  disabled?: boolean;
  hideNone?: boolean;
}) => (
  <BareSelect
    name={name}
    fieldClass={`model_field${extraClass ? ` ${extraClass}` : ''}`}
    value={api.model?.get(name)}
    options={options ?? accList(api.cmsAccessory, cmsPath ?? name)}
    labelKey={labelKey}
    valueKey={valueKey}
    disabled={disabled}
    hideNone={hideNone}
    selectionClass={selectionClass}
    onChange={(raw) => api.commitModelField(name, raw)}
  />
);

/** model_field input row (clamped commit — §49.6 validatedNumberFields). */
export const AInput = ({
  api,
  name,
  label,
  unit,
  required,
  formatter = 'decimal',
  readOnly,
  extraClass,
  rowClass,
  min,
  max,
  children,
}: BaseProps & {
  formatter?: 'decimal' | 'integer' | 'none';
  readOnly?: boolean;
  extraClass?: string;
  rowClass?: string;
  min?: number;
  max?: number;
  children?: React.ReactNode;
}) => (
  <InputRow
    name={name}
    fieldClass="model_field"
    value={api.model?.get(name)}
    label={label}
    unit={unit}
    formatter={formatter === 'none' ? undefined : formatter}
    required={required}
    readOnly={readOnly}
    extraClass={extraClass}
    rowClass={rowClass}
    min={min}
    max={max}
    onCommit={(v) =>
      api.commitModelField(
        name,
        v,
        formatter === 'none' ? undefined : { min, max, formatter }
      )
    }
  >
    {children}
  </InputRow>
);

/** bare model_field input. */
export const ABareInput = ({
  api,
  name,
  valueName,
  formatter = 'decimal',
  readOnly,
  extraClass,
  fieldClass = 'model_field',
  min,
  max,
  style,
}: {
  api: AccessoryFormApi;
  name: string;
  valueName?: string;
  formatter?: 'decimal' | 'integer' | 'none';
  readOnly?: boolean;
  extraClass?: string;
  fieldClass?: string;
  min?: number;
  max?: number;
  /** inline style — a few legacy tmpls nudge a bare control with
   * `style="margin-left: …"` and supply it from no class (drain_pan
   * form.tmpl:30). */
  style?: React.CSSProperties;
}) => (
  <BareInput
    name={name}
    fieldClass={fieldClass}
    value={api.model?.get(valueName ?? name)}
    formatter={formatter === 'none' ? undefined : formatter}
    readOnly={readOnly}
    extraClass={extraClass}
    min={min}
    max={max}
    style={style}
    onCommit={(v) =>
      api.commitModelField(
        name,
        v,
        formatter === 'none' ? undefined : { min, max, formatter }
      )
    }
  />
);

/** catalogue params select — value from the live filter (§49.4 binding). */
export const APSelect = ({
  api,
  target,
  name,
  label,
  unit,
  required,
  cmsPath,
  labelKey,
  valueKey,
  options,
  rowClass,
  extraClass,
  selectionClass,
  disabled,
  hideNone,
  noneMode,
  children,
}: {
  api: AccessoryFormApi;
  target: string;
  name: string;
  label?: React.ReactNode;
  unit?: string;
  required?: boolean;
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  options?: OptionRow[];
  rowClass?: string;
  extraClass?: string;
  selectionClass?: string;
  disabled?: boolean;
  hideNone?: boolean;
  noneMode?: 'visible' | 'hidden' | 'omit';
  children?: React.ReactNode;
}) => {
  const engine = api.catalogueFields[target];
  const compName = engine?.config.componentName ?? 'component';
  return (
    <SelectRow
      name={name}
      fieldClass={`${compName}_params_field${extraClass ? ` ${extraClass}` : ''}`}
      value={engine?.paramValue(name)}
      options={options ?? accList(api.cmsAccessory, cmsPath ?? name)}
      labelKey={labelKey}
      valueKey={valueKey}
      label={label}
      unit={unit}
      required={required}
      disabled={disabled || api.filterFieldsDisabled}
      rowClass={rowClass ?? 'single-line'}
      selectionClass={selectionClass}
      hideNone={hideNone}
      noneMode={noneMode}
      onChange={(raw) => api.commitParamsField(target, name, raw)}
    >
      {children}
    </SelectRow>
  );
};

/** readonly params echo input (jbox NEMA Type / enclosure dims rows). */
export const ParamsEcho = ({
  api,
  target,
  field,
}: {
  api: AccessoryFormApi;
  target: string;
  field: string;
}) => {
  const engine = api.catalogueFields[target];
  const v = engine?.paramValue(field);
  return (
    <input type="text" value={String(v ?? '')} readOnly tabIndex={-1} onChange={() => undefined} />
  );
};

/**
 * Accessory subtab li (§52.1 grammar, ACCESSORY variant): the FIRST subtab
 * has NO label and its sublist carries `max-height-auto` (always visible via
 * CSS styles.css:4640 — max-height unset); labeled subtabs reveal through
 * `input:checked + label + .sublist`. Children are ALWAYS rendered (legacy
 * Handlebars renders the whole form; CSS does the show/hide) — this also
 * keeps per-subtab scroll state alive (parity 308).
 */
export const AccSubtab = ({
  api,
  type,
  cid,
  name,
  stateKey,
  inputId,
  label,
  labelExtra,
  liClass,
  sublistClass,
  headerChrome,
  children,
}: {
  api: AccessoryFormApi;
  type: string;
  cid: number | string;
  name: string;
  /** subtab state key when it differs from data-subtab (legacy copy-paste
   * quirks: dog_house/uda remarks inputs carry data-subtab="performance"). */
  stateKey?: string;
  /** id override (legacy quirks: light electrical id has no underscore). */
  inputId?: string;
  label?: React.ReactNode;
  labelExtra?: React.ReactNode;
  liClass?: string;
  sublistClass?: string;
  headerChrome?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const key = stateKey ?? name;
  const id = inputId ?? `${type}_${cid}_${name}`;
  const open = !!api.subtabs[key];
  return (
    <li className={liClass}>
      {headerChrome}
      <input
        id={id}
        data-subtab={name}
        type="checkbox"
        checked={open}
        onChange={(e) => api.toggleSubtab(key, e.target.checked)}
      />
      {label != null && (
        <label htmlFor={id}>
          {label}
          <i className="icon-add" />
          {labelExtra}
        </label>
      )}
      <ul className={`sublist${sublistClass ? ` ${sublistClass}` : ''}`}>{children}</ul>
    </li>
  );
};

/**
 * 6e2b-2 — the selector-driven accessory "Select" heading (damper/louver/
 * opening/hood/uvc form-heading): hidden rating radio (data-id 2) + the
 * `<type> active-mode` label whose click opens the selection dialog (legacy
 * SelectionMode.changeToRatingMode → rating_mode:activate; here the 6e2a
 * rating STUB until the 6e2c selector cut lands).
 */
export const AccessorySelectHeading = ({
  api,
  type,
  headingClass,
  labelText = ' Select',
  extraChrome,
  section,
}: {
  /** form api kept in the prop contract for the 6e2c selector cut (the
   * activation payload will carry current_model/section via the shim). */
  api?: AccessoryFormApi;
  type: string;
  headingClass?: string;
  labelText?: string;
  extraChrome?: React.ReactNode;
  /** The owning section ROW. Legacy hands the whole section model to
   * `rating_mode:activate` (selection_mode.js:211); the shim-derived payload
   * below carries only `{id, ...aw dims}`, so `name` / `tag` / `type` never
   * reach the selector. The UVC selector needs all three — `name`+`tag` for its
   * Project tab, `type` for the target-surface default. Optional, and only the
   * UVC form passes it, so every other accessory's payload is unchanged. */
  section?: Record<string, unknown> | null;
}) => {
  // ★ SECOND CLICK CLOSES (legacy SelectionMode.changeToRatingMode,
  // selection_mode.js:176-183 — the `state == modes.rating` branch every
  // accessory form takes):
  //   if (self.ratingModeActive && self.ratingDialogIsOpened())
  //     self.deactivateRatingMode(...)      // → Dispatcher 'rating_mode:hide'
  //   else self.activateRatingMode();       // → 'rating_mode:activate'
  // This heading only ever emitted `activate`, and the chassis treats an
  // activate for the already-open selector as a no-op (ComponentSelectorDialog
  // renderFromAHU), so re-clicking an accessory's Select did nothing and Cancel
  // was the only way out of the dialog. The component forms and the AHU tab's
  // Special Options button already carry this toggle
  // (useComponentForm.openRatingStub, TabAhu.toggleSpecialOptions); the
  // accessory headings were the remaining gap.
  //
  // `ratingModeActive` is PER-COMPONENT in legacy, hence the name+id compare:
  // clicking a DIFFERENT accessory's Select still switches the dialog over
  // (that row's own SelectionMode has ratingModeActive false, so it activates).
  // `ratingDialogIsOpened()` is ported verbatim (:185-187) so a stale gate
  // cannot emit a `hide` while no dialog is on screen.
  //
  // force=false: legacy passes `last_opened != model.id`, and `last_opened` is
  // the id this very label published through `open_rating_popup` when it opened
  // — so a same-accessory re-click is an UNGUARDED hide, closing even with
  // unsaved edits in the dialog. Emitting `rating_mode:hide` straight at the
  // chassis (instead of re-entering renderFromAHU) is what preserves that.
  const ratingOpen = useAppSelector((s) => s.ahuBoard.rating.open);
  const onSelectClick = () => {
    const id = api?.model?.id;
    if (
      id != null &&
      ratingOpen?.name === type &&
      ratingOpen?.modelId === id &&
      document.querySelector('.main-rotation')?.classList.contains('rotate')
    ) {
      ahuBoardBus.emit('rating_mode:hide');
      return;
    }
    // SelectionMode rating activation — the 6e2c-3 accessory selector
    // consumes this. The activation MUST carry current_model:{id} (the
    // accessory row id — drives staging + the §30.4 fan-out key
    // <type>_<id>:selector:*) + section (staging dims + IW). Without
    // the row id the chassis falls back to the AHU id and the selector
    // can't resolve its form shim (the evap_pack-orphan lesson).
    ahuBoardBusEmitRating(type, api, section);
  };
  return (
    <div className={`form-heading${headingClass ? ` ${headingClass}` : ''}`}>
      <div className="ahu-header-btns sync-result">
        {extraChrome}
        <div>
          <input
            type="radio"
            name="select_mode_id"
            className="select_mode_radio hidden"
            data-id="2"
            data-value="rating"
            data-name={type}
            readOnly
            checked={false}
          />
          <label className={`${type} active-mode`} onClick={onSelectClick}>
            <i className="icon-rating" />
            {labelText}
          </label>
        </div>
      </div>
    </div>
  );
};

function ahuBoardBusEmitRating(
  type: string,
  api?: AccessoryFormApi,
  sectionRow?: Record<string, unknown> | null
): void {
  const id = api?.model?.id;
  // 6e2c-3 fix — legacy passes the FULL section model on rating_mode:activate
  // (selection_mode.js:211); the React opener has only {id}. Enrich the section
  // payload with the airway dims the selector needs for the "Available Airway"
  // readonly fields + the §51.4 mounting math. Pulled off the registered form
  // shim's getSectionDims (the form's own awDimsRef derivation).
  const dims =
    id != null ? window.__FORM_SHIM__?.[id]?.getSectionDims?.() : undefined;
  // `name`/`tag`/`type` come from the caller-supplied row (legacy's full section
  // model); the shim's dims stay authoritative for the airway numbers.
  const section = api?.model
    ? {
        ...(sectionRow ?? {}),
        id: Number(api.model.get('section_id')),
        ...(dims ?? {}),
      }
    : undefined;
  ahuBoardBus.emit('rating_mode:activate', {
    name: type,
    // current_model:{id} → the accessory row id (staging + fan-out key). The
    // section carries aw dims (aw_width/aw_height/aw_length) + IW context.
    // COMBO LOUVER (KA-5245) — A1.1: `is_combo` rides along because the CHASSIS
    // header needs it. Legacy hands the whole Backbone model over and reads
    // `self.current_model?.get('is_combo')` when rendering the tab strip
    // (component_selector.js:94 → component_selector.tmpl:47
    // `{{#if is_combo}}Combo {{/if}}Louver`); the React payload is a bag, so the
    // one field the chassis reads is added explicitly.
    current_model:
      id != null ? { id, is_combo: !!api?.model?.get('is_combo') } : undefined,
    section,
  });
  ahuBoardBus.emit('open_rating_popup');
}

/**
 * 6e2b-2 — the Desired Face Velocity + auto-size row (damper/louver/hood/
 * opening performance sublists; §49.6 onAutoSze / parity 331). The input
 * persists per-accessory in localStorage (#349); the button greys without
 * airflow and disables while the auto-size snapshot is unchanged.
 */
export const AutoSizeRow = ({ api, tooltip }: { api: AccessoryFormApi; tooltip: string }) => (
  <li>
    <div>
      {/* §JSX-WS (complexMounting.tsx:204) — hood form.tmpl:469-473 and the
          damper/louver/opening equivalents are identical here: a newline
          between `</span>`, the input and the auto-size button, i.e. two real
          ~4px spaces that JSX drops. */}
      <span className="unit short-unit">fpm</span>{' '}
      <input
        type="text"
        data-formatter_type="integer"
        autoComplete="off"
        data-min="0"
        name="desired_face_velocity_actual"
        // legacy bindFormatter renders integers with thousands separators
        // ("1,200" — 6e2b verifier parity 331); commits strip the commas.
        value={formatIntegerDisplay(api.desiredFaceVelocity) || api.desiredFaceVelocity}
        className="coli-medium"
        onChange={(e) => api.setDesiredFaceVelocity(e.target.value.replace(/,/g, ''))}
      />{' '}
      <button
        type="button"
        className={`btn auto-size ka-has-tooltip ka-tooltip-left${
          !api.model?.get('airflow_actual') ? ' non-clickable' : ''
        }`}
        disabled={api.autoSizeDisabled}
        {...{ tooltip }}
        onClick={() => api.runAutoSize(true)}
      >
        auto-size
      </button>
      <span className="label">Desired Face Velocity</span>
    </div>
  </li>
);

/**
 * form-heading chrome (§52.1 catalogue chrome / parity 347): count badge
 * "<count> / <full>", btn-sync vis-<type> + warning/synced classes, reset
 * span — verbatim per the per-type tmpl headings.
 */
export const AccessoryHeading = ({
  api,
  target,
  visClass,
  wrapperClass = 'ahu-header-btns sync-result',
}: {
  api: AccessoryFormApi;
  target: string;
  visClass: string;
  wrapperClass?: string;
}) => {
  const engine = api.catalogueFields[target];
  if (!engine) return <div className="form-heading" />;
  return (
    <div className="form-heading">
      <CatalogueChrome
        componentName={engine.config.componentName}
        componentsName={engine.config.componentsName}
        count={engine.cmsCount}
        fullCount={engine.cmsFullCount}
        visClass={visClass}
        wrapperClass={wrapperClass}
        searchResultClass="search-result"
        onToggleList={() => void engine.toggleSmartList()}
        onReset={() => engine.deselectModel()}
      />
    </div>
  );
};
